import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Form, Select, Spin } from 'antd'
import { debounce } from 'lodash'
import { getSystemAgentsList, getSystemAgentDetail } from '@/service/systemControl'
import { renderTextStr, renderPhoneStr } from '@/utils/renderUtils'

/** GET /system/agents/page — 在职 + 分页 + 可按 keyword 姓名/手机号/账号检索 */
export const STAFF_AGENT_TARGET_LIST_QUERY = {
  pageNumber: 1,
  pageSize: 20,
  orderBy: 'create_time',
  orderDirection: 'desc',
  state: 1
}

/** NotificationTargetType 是否「员工」（走 agents/page 选人）——与后端枚举 EMPLOYEE 等对齐 */
export function notificationTargetIsStaff (targetType) {
  if (targetType === undefined || targetType === null || targetType === '') return false
  const u = String(targetType).trim().toUpperCase()
  return u === 'EMPLOYEE' || u === 'AGENT' || u === 'STAFF'
}

function normalizeAgentRecords (body) {
  if (!body || typeof body !== 'object') return []
  const list = Array.isArray(body.list)
    ? body.list
    : Array.isArray(body.records)
      ? body.records
      : Array.isArray(body.data?.list)
        ? body.data.list
        : Array.isArray(body.data?.records)
          ? body.data.records
          : []
  return list
}

export function agentToTargetOption (a) {
  if (!a || a.id == null) return null
  const name = renderTextStr(a.userName)
  const phone = a.phone != null ? String(a.phone).trim() : ''
  const left = name || `#${String(a.id)}`
  const label = phone ? `${left} · ${renderPhoneStr(phone)}` : left
  return {
    value: String(a.id),
    label,
    phone,
    agent: a
  }
}

/**
 * 列表已有 targetName/phone（编辑回填）时用，与 agentToTargetOption 展示一致，避免单靠详情接口才有 label。
 */
export function seedStaffTargetPickOption ({ targetId, targetName: nameRaw, phone: phoneRaw }) {
  if (targetId === undefined || targetId === null || targetId === '') return null
  const idStr = String(targetId)
  const name = nameRaw !== undefined && nameRaw !== null ? String(nameRaw).trim() : ''
  if (name === '') return null
  const phone = phoneRaw !== undefined && phoneRaw !== null ? String(phoneRaw).trim() : ''
  const left = name || `#${idStr}`
  const label = phone !== '' ? `${left} · ${renderPhoneStr(phone)}` : left
  return { value: idStr, label, phone: phone !== '' ? phone : '' }
}

/**
 * Form.List「定向目标」行内：在职员工下拉（联动门店 + keyword），选中回填 targetId / 手机号 / 展示名。
 * 须在 antd Form 子树中使用（依赖 useFormInstance）。
 */
function StaffAgentTargetPick ({
  storeId,
  disabled,
  value,
  onChange,
  listIndex
}) {
  const form = Form.useFormInstance()
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const seqRef = useRef(0)
  const optionsRef = useRef([])
  optionsRef.current = options

  const loadAgents = useCallback(
    async (keywordRaw) => {
      if (storeId === undefined || storeId === null || storeId === '') {
        setOptions([])
        return
      }
      const kw = typeof keywordRaw === 'string' ? keywordRaw.trim() : ''
      const seq = ++seqRef.current
      setLoading(true)
      try {
        const res = await getSystemAgentsList({
          ...STAFF_AGENT_TARGET_LIST_QUERY,
          storeId,
          ...(kw !== '' ? { keyword: kw } : {})
        })
        if (seq !== seqRef.current) return
        const list = normalizeAgentRecords(res?.data ?? {})
        const next = list
          .map((a) => agentToTargetOption(a))
          .filter(Boolean)
        setOptions(next)
      } catch (e) {
        if (seq === seqRef.current) {
          setOptions([])
        }
      } finally {
        if (seq === seqRef.current) {
          setLoading(false)
        }
      }
    },
    [storeId]
  )

  const debouncedLoad = useMemo(() => debounce((k) => loadAgents(k), 300), [loadAgents])

  useEffect(() => {
    loadAgents('')
    return () => debouncedLoad.cancel()
  }, [loadAgents, debouncedLoad])

  useEffect(() => () => debouncedLoad.cancel(), [debouncedLoad])

  useEffect(() => {
    if (value === undefined || value === null || value === '') return
    const idStr = String(value)
    if (optionsRef.current.some((o) => o.value === idStr)) return
    const targets = form.getFieldValue('targets') || []
    const row = targets[listIndex]
    const seedOpt = seedStaffTargetPickOption({
      targetId: value,
      targetName:
        row && row.targetName !== undefined && row.targetName !== null
          ? row.targetName
          : row && row.target_name !== undefined && row.target_name !== null
            ? row.target_name
            : '',
      phone: row && row.phone
    })
    if (seedOpt) {
      setOptions((prev) => (prev.some((o) => o.value === seedOpt.value) ? prev : [...prev, seedOpt]))
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await getSystemAgentDetail(idStr)
        const d = res?.data
        if (cancelled || !d || d.id == null) return
        const opt = agentToTargetOption(d)
        if (!opt) return
        setOptions((prev) => (prev.some((o) => o.value === opt.value) ? prev : [...prev, opt]))
        const rows = form.getFieldValue('targets') || []
        const r = rows[listIndex]
        if (r && (!r.phone || String(r.phone).trim() === '') && opt.phone) {
          const next = rows.map((t, idx) =>
            idx === listIndex ? { ...t, phone: opt.phone } : t
          )
          form.setFieldsValue({ targets: next })
        }
      } catch (e) {
        // 详情失败时仍保留 ID，由用户重新搜索
      }
    })()
    return () => {
      cancelled = true
    }
  }, [value, listIndex, storeId, form])

  const selectValue = value !== undefined && value !== null && value !== '' ? String(value) : undefined

  const patchTargetsAux = (patch) => {
    const targets = [...(form.getFieldValue('targets') || [])]
    const row = targets[listIndex]
    if (!row) return
    targets[listIndex] = { ...row, ...patch }
    form.setFieldsValue({ targets })
  }

  const handleChange = (v, opt) => {
    if (v === undefined || v === null || v === '') {
      if (typeof onChange === 'function') onChange(undefined)
      patchTargetsAux({ phone: '', targetName: '' })
      return
    }
    const n = Number(v)
    if (typeof onChange === 'function') {
      onChange(Number.isFinite(n) ? n : undefined)
    }
    const idStr = String(v)
    const fromOpt =
      opt && typeof opt === 'object' && 'phone' in opt ? String(opt.phone ?? '').trim() : ''
    const fromList = options.find((o) => o.value === idStr)?.phone
    const phoneStr = fromOpt || (fromList ? String(fromList).trim() : '')
    const nameFromAgent =
      opt && typeof opt === 'object' && opt.agent ? renderTextStr(opt.agent.userName) : ''
    patchTargetsAux({
      phone: phoneStr,
      ...(nameFromAgent !== '' ? { targetName: nameFromAgent } : {})
    })
  }

  const storeMissing = storeId === undefined || storeId === null || storeId === ''

  return (
    <Select
      showSearch
      allowClear
      filterOption={false}
      placeholder={storeMissing ? '请先选择门店' : '搜索姓名/手机号/账号'}
      disabled={disabled || storeMissing}
      loading={loading}
      notFoundContent={loading ? <Spin size="small" /> : null}
      options={options}
      value={selectValue}
      onChange={handleChange}
      onSearch={(s) => {
        setLoading(true)
        debouncedLoad(s || '')
      }}
      onDropdownVisibleChange={(open) => {
        if (open && !storeMissing) {
          loadAgents('')
        }
      }}
      style={{ minWidth: 260, width: '100%' }}
    />
  )
}

StaffAgentTargetPick.propTypes = {
  storeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  listIndex: PropTypes.number.isRequired
}

StaffAgentTargetPick.defaultProps = {
  storeId: undefined,
  disabled: false,
  value: undefined,
  onChange: undefined
}

export default StaffAgentTargetPick
